import { useEffect, useMemo, useRef, useState } from 'react';
import { Upload, Check, AlertTriangle } from 'lucide-react';
import { uploadOpenPlayScreenshot } from '../utils/openPlayUtil';
import { formatMoney } from '../utils/currencyUtil';
import { formatCountdown, msUntil } from '../utils/countdownUtil';
import { hasReceipt } from '../utils/receiptUtil';
import { PAYMENT_STEP_LABELS } from '../constants/bookingConst';
import OpenPlaySessionDetails from './OpenPlaySessionDetails';
import OpenPlayRoster from './OpenPlayRoster';
import PaymentQrDisplay from './PaymentQrDisplay';
import { ENTITY_TYPES, GUEST_ACTOR } from '../utils/auditLogUtil';
import '../css/UserInfoForm.css';
import '../css/CheckoutSummary.css';
import '../css/OpenPlayModal.css';

/**
 * The Open Play payment step — QR, countdown, receipt upload — shown in the
 * Open Play checkout tab (OpenPlayCheckoutPage). Moved out of OpenPlayModal
 * when checkout moved to its own tab; the layout and wording are unchanged,
 * plus the same Step 1 / Step 2 headings as the court-booking checkout.
 *
 * @param {Object} props
 * @param {Object} props.join - the openPlayJoins record being paid for (with `id`).
 * @param {Object} props.context - { session, sportName, pricePerPlayer, dateStr, timeSlot } from OpenPlayModal.
 * @param {Object} props.config - club config (QR codes, court name, currency).
 * @param {(join: Object) => void} props.onReceiptUploaded - receipt stored; receives the updated join.
 * @param {() => void} props.onDone - "Done" after the receipt is in.
 * @param {() => void} props.onBack - back to the player form (the unpaid join is discarded).
 * @returns {JSX.Element}
 */
function OpenPlayCheckoutSummary({ join, context, config, onReceiptUploaded, onDone, onBack }) {
  const peso = (n) => formatMoney(n, config);
  const fileInputRef = useRef(null);
  const [screenshot, setScreenshot] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  // Starts true when the tab is reloaded after the receipt went in.
  const [uploadSuccess, setUploadSuccess] = useState(() => hasReceipt(join));
  const [msLeft, setMsLeft] = useState(() => msUntil(join?.paymentDeadline));

  const paymentMethods = useMemo(() => {
    if (config?.qrCodes && config.qrCodes.length > 0) {
      return config.qrCodes.map(qr => ({ id: qr.id, name: qr.name || 'Payment QR', url: qr.url }));
    }
    if (config?.paymentQrUrl) {
      return [{ id: 'legacy-qr', name: config?.bankName || 'Payment', url: config.paymentQrUrl }];
    }
    return [];
  }, [config]);
  const [selectedQrId, setSelectedQrId] = useState('');
  useEffect(() => {
    if (paymentMethods.length > 0 && !paymentMethods.some(m => m.id === selectedQrId)) {
      setSelectedQrId(paymentMethods[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethods]);
  const selectedMethod = paymentMethods.find(m => m.id === selectedQrId) || paymentMethods[0];

  // A "Download QR" is recorded against this join, like its other entries.
  const qrAudit = useMemo(() => (join?.id ? {
    entityType: ENTITY_TYPES.OPENPLAY_JOIN,
    entityId: join.id,
    performedBy: join.fullName || GUEST_ACTOR,
    summary: { confirmationId: join.confirmationId || null, date: join.date || null },
  } : null), [join]);

  // Live payment countdown, same pattern as CheckoutSummary.jsx.
  useEffect(() => {
    if (!join?.paymentDeadline || uploadSuccess) return undefined;
    const tick = () => setMsLeft(msUntil(join.paymentDeadline));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [join?.paymentDeadline, uploadSuccess]);

  /**
   * Stores the picked receipt. Uploading is what submits the join — see
   * uploadOpenPlayScreenshot, which also writes the audit entry.
   * @param {Event} e - file input change event.
   */
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || !join) return;
    setScreenshot(file);
    setUploadError(null);
    setUploading(true);
    try {
      await uploadOpenPlayScreenshot(file, join.confirmationId);
      setUploadSuccess(true);
      onReceiptUploaded?.({ ...join, status: 'pending', hasPaymentScreenshot: true });
    } catch (err) {
      setUploadError(err.message);
      setScreenshot(null);
    } finally {
      setUploading(false);
    }
  };

  const expired = msLeft !== null && msLeft <= 0 && !uploadSuccess;
  const detailsProps = {
    session: context?.session || null,
    config,
    sportName: context?.sportName || join?.sportName || 'Court',
    dateStr: context?.dateStr || join?.date || null,
    timeSlot: context?.timeSlot || join?.timeSlot || null,
    pricePerPlayer: Number(context?.pricePerPlayer ?? join?.pricePerPlayer) || 0,
  };

  return (
    <div className="user-info-form checkout-summary">
      <div className="uif-card cs-card">
        <div className="uif-head">
          <h2 className="uif-title">Checkout</h2>
          {/* Before the receipt this is the same as Back (the unpaid join is
              thrown away); after it, the join is submitted and × finishes. */}
          <button
            type="button"
            className="uif-close"
            onClick={uploadSuccess ? onDone : onBack}
            aria-label="Close"
            title="Close"
          >&times;</button>
        </div>

        {/* Same summary as the join screen, minus the notes/RSVP blurb. */}
        <OpenPlaySessionDetails {...detailsProps} showNotes={false} />
        <OpenPlayRoster join={join} />

        <div className="uif-total-row">
          <span>{join.numPlayers} player{join.numPlayers === 1 ? '' : 's'} &times; {peso(detailsProps.pricePerPlayer)}</span>
          <span className="uif-total-amt">{peso(join.totalAmount)}</span>
        </div>

        {expired ? (
          <>
            <p className="cs-note cs-note-amber">
              Your payment window has expired. Please start over to try again.
            </p>
            <button type="button" className="uif-continue" onClick={onBack}>
              Start Over
            </button>
          </>
        ) : (
          <>
            <div className="cs-step-label" role="heading" aria-level={3}>{PAYMENT_STEP_LABELS.PAY}</div>

            {msLeft !== null && !uploadSuccess && (
              <div className="cs-timer">
                Complete payment within <strong className="cs-timer-count">{formatCountdown(msLeft)}</strong> or you'll need to submit again.
              </div>
            )}

            {paymentMethods.length > 0 ? (
              <>
                <div className="cs-qr-label">Choose payment QR</div>
                <select
                  className="cs-qr-select"
                  value={selectedQrId}
                  onChange={(e) => setSelectedQrId(e.target.value)}
                >
                  {paymentMethods.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
                {selectedMethod && (
                  <PaymentQrDisplay key={selectedMethod.id} method={selectedMethod} audit={qrAudit} />
                )}
                <p className="cs-note cs-note-amber">
                  Before you send, please double-check you're paying to the account shown in the QR above.
                </p>
              </>
            ) : (
              <p className="cs-note cs-note-amber">
                No payment QR codes have been configured yet. Please contact {config?.courtName || 'us'} directly to arrange payment.
              </p>
            )}

            <div className="cs-step-label" role="heading" aria-level={3}>{PAYMENT_STEP_LABELS.UPLOAD}</div>

            <div className="cs-ref-block">
              <div className="cs-ref-label">Reference</div>
              <div className="cs-ref-value">{join.confirmationId}</div>
              <div className="cs-ref-note">Please include this when you send your screenshot.</div>
            </div>

            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className={`cs-upload-btn ${uploadSuccess ? 'cs-upload-btn-done' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploadSuccess ? (
                <><Check size={16} /> Receipt uploaded{screenshot ? ` — ${screenshot.name}` : ''}</>
              ) : uploading ? (
                'Uploading…'
              ) : (
                <><Upload size={16} /> Upload Payment Receipt</>
              )}
            </button>
            {uploadError && <p className="cs-upload-error">{uploadError}</p>}

            {uploadSuccess ? (
              <button type="button" className="cs-payment-done-btn" onClick={onDone}>
                Done
              </button>
            ) : (
              // An unpaid join is not on the roster and holds no spot (see
              // ACTIVE_JOIN_STATUSES), so a guest who wanders off at this
              // screen can come back to a full session.
              <p className="cs-note cs-note-amber cs-note-warning">
                <AlertTriangle size={15} /> Upload your payment receipt to submit your Open Play join request —
                your spot isn&rsquo;t held until you do.
              </p>
            )}

            {/* Leaving is the fallback, not the offer — so it sits last. */}
            {!uploadSuccess && (
              <button
                type="button"
                className="opm-back-btn"
                onClick={onBack}
                disabled={uploading}
              >
                Back
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default OpenPlayCheckoutSummary;
